import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import submissionService from 'services/submission';
import { compareCanvasRubrics, getAIPreview, interleave, stripHTML } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const {
        course,
        setAssignments,
        currentAssignment: originalCurrentAssignment,
        previewSubmissions: allPreviewSubmissions,
        setPreviewSubmissions,
    } = useApp();
    const { auth, getAccount } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { id: currentAssignmentId, submissionId: currentSubmissionId } = useParams();
    const { message } = useSnackbar();
    const { onMessage } = useWebsocket();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState({
        criteria: {},
        feedback: null,
        inline_comments: null,
        grading_explanations: {},
    });
    const [gradeOverrides, setGradeOverrides] = useState({});
    const [meta, setMeta] = useState({ hasSeenFeedback: true });
    const [tab, setTab] = useState('grades');
    const [selectedCriterionId, setSelectedCriterionId] = useState(null);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [selectedInlineCommentId, setSelectedInlineCommentId] = useState(null);
    const [isGradesGenerating, setIsGradesGenerating] = useState(false);
    const [isFeedbackGenerating, setIsFeedbackGenerating] = useState(false);
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    const [fineTuneDialog, setFineTuneDialog] = useState({ open: false, expanded: 'rubric', changes: null });
    const [pushRubricDialog, setPushRubricDialog] = useState({ open: false });
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);

    const currentAssignment = useMemo(() => {
        if (!originalCurrentAssignment) return originalCurrentAssignment;
        return {
            ...originalCurrentAssignment,
            ...(fineTuneDialog.changes && {
                rubric: {
                    ...originalCurrentAssignment.rubric,
                    criteria: fineTuneDialog.changes.rubric.criteria,
                    sort: fineTuneDialog.changes.rubric.sort,
                },
                ai_grading_context: fineTuneDialog.changes.ai_grading_context
                    ? fineTuneDialog.changes.ai_grading_context
                    : originalCurrentAssignment.ai_grading_context,
                ai_feedback_context: fineTuneDialog.changes.ai_feedback_context
                    ? fineTuneDialog.changes.ai_feedback_context
                    : originalCurrentAssignment.ai_feedback_context,
            }),
        };
    }, [fineTuneDialog.changes, originalCurrentAssignment]);
    const previewSubmissions = allPreviewSubmissions
        ?.filter(({ ai, is_failed }) => Boolean(ai) || is_failed)
        ?.sort((a, b) => {
            const aName = a.files[0].name.toLowerCase();
            const bName = b.files[0].name.toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
        });
    const currentSubmission = previewSubmissions?.find(({ id }) => id === currentSubmissionId);

    const selectedCriterionTitle = currentAssignment?.rubric?.criteria?.find(
        ({ id }) => id === selectedCriterionId
    )?.title;

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? [])
                .filter((item) => {
                    if (typeof item === 'string') return true;
                    return false;
                })
                .map((item) => {
                    return {
                        id: selectedInlineCommentId ?? selectedCriterionId ?? selectedFeedbackId,
                        value: typeof item === 'string' ? item : item.highlight,
                    };
                });
        };

        if (tab === 'grades' && selectedCriterionId) {
            const locations = currentSubmission?.ai?.grading?.[selectedCriterionId]?.locations;
            return format(locations);
        }

        if (tab === 'feedback' && selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId);
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        const comments = form.inline_comments ?? [];
        const activeHighlights = comments
            .filter(({ active }) => active)
            .filter(({ id }) => id === selectedInlineCommentId)
            .map(({ highlight }) => highlight);

        return format(activeHighlights);
    }, [
        currentSubmission?.ai?.feedback,
        currentSubmission?.ai?.grading,
        form.feedback?.improvement_suggestions,
        form.inline_comments,
        selectedCriterionId,
        selectedFeedbackId,
        selectedInlineCommentId,
        tab,
    ]);

    const goToNextSubmission = () => {
        let nextSubmissionIndex = previewSubmissions.findIndex(({ id }) => id === currentSubmission.id) + 1;
        if (nextSubmissionIndex === previewSubmissions.length) nextSubmissionIndex = 0;
        const nextSubmission = previewSubmissions[nextSubmissionIndex];
        const nextSubmissionId = nextSubmission.id;
        history.push(`/assignments/${currentAssignmentId}/preview/${nextSubmissionId}`);
        setForm({
            criteria: nextSubmission?.grading?.criteria ?? {},
            feedback: nextSubmission?.ai?.feedback ?? null,
            inline_comments: nextSubmission?.ai?.inline_comments ?? null,
            grading_explanations: nextSubmission?.grading_explanations ?? {},
        });
        setMeta({ hasSeenFeedback: nextSubmission.has_seen_feedback ?? false });
    };

    const handleClickNext = () => {
        goToNextSubmission();
    };

    const handleSelectSubmission = (submissionId) => {
        history.push(`/assignments/${currentAssignmentId}/preview/${submissionId}`);
    };

    const handleClickRegenerateFeedback = () => {
        setDialog({
            open: true,
            message: t('dialog.regenerate-suggested-feedback.title', 'Regenerate Suggested Feedback?'),
            description: t(
                'dialog.regenerate-suggested-feedback.description',
                'This will delete any existing feedback permanently.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.regenerate', 'Regenerate'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setIsFeedbackGenerating(true);
                const account = await getAccount();
                await getAIPreview({
                    target: 'feedback',
                    course_id: course.id,
                    owner_account_id: currentAssignment.owner_account_id,
                    assignment: currentAssignment,
                    submission: {
                        ...currentSubmission,
                        grading: omit(form, ['grading_explanations']),
                        ...(Object.keys(form.grading_explanations).length && {
                            grading_explanations: form.grading_explanations,
                        }),
                    },
                    connections: account.connections.map(({ id }) => id),
                    language: course.language ?? 'English',
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleSelectDropdown = async ({ key }) => {
        if (key === 'regenerate-grades') {
            setDialog({
                open: true,
                message: t('dialog.regenerate-suggested-grades.title', 'Regenerate Suggested Grades?'),
                description: t(
                    'dialog.regenerate-suggested-grades.description',
                    'This will delete any existing grades permanently.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.regenerate', 'Regenerate'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: async () => {
                    closeDialog();
                    setIsGradesGenerating(true);
                    try {
                        const account = await getAccount();
                        await getAIPreview({
                            target: 'grading',
                            course_id: course.id,
                            owner_account_id: currentAssignment.owner_account_id,
                            assignment: currentAssignment,
                            submission: currentSubmission,
                            connections: account.connections.map(({ id }) => id),
                            language: course.language ?? 'English',
                        });
                    } catch (e) {
                        console.error(e);
                        message.error(t('common:message.something-went-wrong', 'Something went wrong.'));
                    }
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
        if (key === 'regenerate-feedback') {
            handleClickRegenerateFeedback();
        }
        if (key === 'copy-all') {
            let feedback = '';
            if (typeof currentSubmission.ai?.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else {
                feedback += stripHTML(form.feedback.commentary);
                if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                    form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                        if (index || form.feedback.commentary) {
                            feedback += '\n\n';
                        }
                        const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                        feedback += `${criterion.title}\n\n`;
                        feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                        feedback +=
                            !positive.active && positive.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : positive.text === ''
                                  ? t(
                                        'feedback-section.no-feedback-no-pushback',
                                        'No feedback provided. This message will not be passed back.'
                                    )
                                  : stripHTML(positive.text);
                        feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                        feedback +=
                            !negative.active && negative.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : negative.text === ''
                                  ? t(
                                        'feedback-section.no-feedback-no-pushback',
                                        'No feedback provided. This message will not be passed back.'
                                    )
                                  : stripHTML(negative.text);
                    });
                } else if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                    form.feedback.improvement_suggestions
                        .filter(({ active }) => active)
                        .forEach(({ text }, index) => {
                            if (index || form.feedback.commentary) {
                                feedback += '\n\n';
                            }
                            feedback += stripHTML(text);
                        });
                }
            }
            await navigator.clipboard.writeText(feedback);
            message.success(t('message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
                description: t(
                    'dialog.delete-all-feedback.description',
                    'Are you sure you want to delete all feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        positive: {
                                            ...item.positive,
                                            active: false,
                                        },
                                        negative: {
                                            ...item.negative,
                                            active: false,
                                        },
                                    })),
                                },
                            };
                        });
                    }
                    if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        active: false,
                                    })),
                                },
                            };
                        });
                    }
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickExit = () => {
        const timestamp = new Date().getTime();
        if (Boolean(fineTuneDialog.changes)) {
            setDialog({
                open: true,
                message: t('dialog.exit-preview.title', 'Exit Preview'),
                description: t(
                    'dialog.exit-preview.description',
                    'If you exit without applying changes, any adjustments made during fine-tuning will not be saved, and your assignment will remain unchanged.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: async () => {
                    closeDialog();
                    history.push(`/assignments/${currentAssignmentId}`);
                    setAssignments((current) => {
                        return current.map((a) => {
                            if (a.id !== currentAssignmentId) return a;
                            const updatedAssignment = {
                                ...a,
                                modified_on: timestamp,
                                has_seen_preview: true,
                            };
                            assignmentService.updateAssignment(currentAssignmentId, omit(updatedAssignment, ['_id']));
                            return updatedAssignment;
                        });
                    });
                    const previewSubmissions = await assignmentService.listAssignmentSubmissions(
                        currentAssignmentId,
                        JSON.stringify({ type: 'fine-tune' })
                    );
                    setPreviewSubmissions(previewSubmissions);
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            setAssignments((current) => {
                return current.map((a) => {
                    if (a.id !== currentAssignmentId) return a;
                    const updatedAssignment = {
                        ...a,
                        modified_on: timestamp,
                        has_seen_preview: true,
                    };
                    assignmentService.updateAssignment(currentAssignmentId, omit(updatedAssignment, ['_id']));
                    return updatedAssignment;
                });
            });
            history.push(`/assignments/${currentAssignmentId}`);
        }
    };

    const handleClickFineTune = () => {
        setFineTuneDialog((current) => ({ ...current, open: true }));
    };

    const handleClosePushRubricDialog = () => {
        setIsApplyingChanges(false);
        closeDialog();
    };

    const handleClickApplyChanges = async () => {
        if (currentAssignment.api_assignment_id && process.env.REACT_APP_KIND === 'CANVAS') {
            setIsApplyingChanges(true);
            const course_id = auth.course_id;
            const accessToken = localStorage.getItem('access_token');
            const APIAssignment = await apiService.getAssignment({
                course_id,
                accessToken,
                assignment_id: currentAssignment.api_assignment_id,
            });
            const is_matching = compareCanvasRubrics(currentAssignment.rubric, APIAssignment.rubric);
            if (!is_matching) {
                return setPushRubricDialog({
                    open: true,
                    onSave: ({ api_data, rubric }) => {
                        return setDialog({
                            open: true,
                            message: t('dialog.apply-fine-tune-changes.title', 'Apply Changes?'),
                            description: t(
                                'dialog.apply-fine-tune-changes.description',
                                'If you’re satisfied with the fine-tuning results, you can apply the changes, and your assignment details will be updated accordingly.'
                            ),
                            note: '',
                            variant: 'primary',
                            actionButtonText: t('common:controls.apply-changes', 'Apply Changes'),
                            cancelButtonText: t('common:controls.cancel', 'Cancel'),
                            onAction: async () => {
                                setIsApplyingChanges(true);
                                const timestamp = new Date().getTime();
                                closeDialog();
                                const updatedAssignment = {
                                    ...currentAssignment,
                                    rubric: rubric,
                                    api_data,
                                    modified_on: timestamp,
                                    ai_grading_settings: null,
                                    has_seen_preview: true,
                                    has_finished_preview: true,
                                };
                                if (originalCurrentAssignment.ai_grading_settings) {
                                    const gradingInstructionsArray = Object.entries(
                                        originalCurrentAssignment.ai_grading_settings.criteria
                                    )
                                        .filter(([_, { grading_instructions }]) => Boolean(grading_instructions))
                                        .map(([criterionId, { grading_instructions }]) => {
                                            const criterionTitle = originalCurrentAssignment.rubric.criteria.find(
                                                ({ id }) => id === criterionId
                                            ).title;
                                            return `<p>${criterionTitle}:</p>${grading_instructions}`;
                                        });
                                    const gradingInstructionsToAdd = interleave(gradingInstructionsArray, '<p></p>');
                                    if (updatedAssignment.ai_grading_context) {
                                        updatedAssignment.ai_grading_context += '<p></p>';
                                    }
                                    updatedAssignment.ai_grading_context += gradingInstructionsToAdd.join('');
                                }
                                setAssignments((current) => {
                                    return current.map((a) => {
                                        if (a.id !== updatedAssignment.id) return a;
                                        return updatedAssignment;
                                    });
                                });
                                await assignmentService.updateAssignment(
                                    updatedAssignment.id,
                                    omit(updatedAssignment, ['_id'])
                                );
                                previewSubmissions.forEach((ps) => {
                                    const updatedSubmission = {
                                        ...ps,
                                        has_seen_feedback: false,
                                        modified_on: timestamp,
                                    };
                                    submissionService.updateSubmission(
                                        updatedSubmission.id,
                                        omit(updatedSubmission, ['_id'])
                                    );
                                });
                                setFineTuneDialog((current) => ({ ...current, changes: null }));
                                message.success(
                                    t(
                                        'message.assignment-updated-success-all',
                                        'All assignment details were successfully updated.'
                                    )
                                );
                                history.push(`/assignments/${currentAssignmentId}`);
                                setIsApplyingChanges(false);
                            },
                            onCancel: () => handleClosePushRubricDialog(),
                            onClose: () => handleClosePushRubricDialog(),
                        });
                    },
                    form: currentAssignment,
                });
            } else {
                setDialog({
                    open: true,
                    message: t('dialog.apply-fine-tune-changes.title', 'Apply Changes?'),
                    description: t(
                        'dialog.apply-fine-tune-changes.description',
                        'If you’re satisfied with the fine-tuning results, you can apply the changes, and your assignment details will be updated accordingly.'
                    ),
                    note: '',
                    variant: 'primary',
                    actionButtonText: t('common:controls.apply-changes', 'Apply Changes'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: async () => {
                        setIsApplyingChanges(true);
                        const timestamp = new Date().getTime();
                        closeDialog();
                        const updatedAssignment = {
                            ...currentAssignment,
                            modified_on: timestamp,
                            ai_grading_settings: null,
                            has_seen_preview: true,
                            has_finished_preview: true,
                        };
                        if (originalCurrentAssignment.ai_grading_settings) {
                            const gradingInstructionsArray = Object.entries(
                                originalCurrentAssignment.ai_grading_settings.criteria
                            )
                                .filter(([_, { grading_instructions }]) => Boolean(grading_instructions))
                                .map(([criterionId, { grading_instructions }]) => {
                                    const criterionTitle = originalCurrentAssignment.rubric.criteria.find(
                                        ({ id }) => id === criterionId
                                    ).title;
                                    return `<p>${criterionTitle}:</p>${grading_instructions}`;
                                });
                            const gradingInstructionsToAdd = interleave(gradingInstructionsArray, '<p></p>');
                            if (updatedAssignment.ai_grading_context) {
                                updatedAssignment.ai_grading_context += '<p></p>';
                            }
                            updatedAssignment.ai_grading_context += gradingInstructionsToAdd.join('');
                        }
                        setAssignments((current) => {
                            return current.map((a) => {
                                if (a.id !== updatedAssignment.id) return a;
                                return updatedAssignment;
                            });
                        });
                        await assignmentService.updateAssignment(
                            updatedAssignment.id,
                            omit(updatedAssignment, ['_id'])
                        );
                        previewSubmissions.forEach((ps) => {
                            const updatedSubmission = {
                                ...ps,
                                has_seen_feedback: false,
                                modified_on: timestamp,
                            };
                            submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        });
                        setFineTuneDialog((current) => ({ ...current, changes: null }));
                        message.success(
                            t(
                                'message.assignment-updated-success-all',
                                'All assignment details were successfully updated.'
                            )
                        );
                        history.push(`/assignments/${currentAssignmentId}`);
                        setIsApplyingChanges(false);
                    },
                    onCancel: () => handleClosePushRubricDialog(),
                    onClose: () => handleClosePushRubricDialog(),
                });
            }
        } else {
            setDialog({
                open: true,
                message: t('dialog.apply-fine-tune-changes.title', 'Apply Changes?'),
                description: t(
                    'dialog.apply-fine-tune-changes.description',
                    'If you’re satisfied with the fine-tuning results, you can apply the changes, and your assignment details will be updated accordingly.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.apply-changes', 'Apply Changes'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: async () => {
                    setIsApplyingChanges(true);
                    const timestamp = new Date().getTime();
                    closeDialog();
                    const updatedAssignment = {
                        ...currentAssignment,
                        modified_on: timestamp,
                        ai_grading_settings: null,
                        has_seen_preview: true,
                        has_finished_preview: true,
                    };
                    if (originalCurrentAssignment.ai_grading_settings) {
                        const gradingInstructionsArray = Object.entries(
                            originalCurrentAssignment.ai_grading_settings.criteria
                        )
                            .filter(([_, { grading_instructions }]) => Boolean(grading_instructions))
                            .map(([criterionId, { grading_instructions }]) => {
                                const criterionTitle = originalCurrentAssignment.rubric.criteria.find(
                                    ({ id }) => id === criterionId
                                ).title;
                                return `<p>${criterionTitle}:</p>${grading_instructions}`;
                            });
                        const gradingInstructionsToAdd = interleave(gradingInstructionsArray, '<p></p>');
                        if (updatedAssignment.ai_grading_context) {
                            updatedAssignment.ai_grading_context += '<p></p>';
                        }
                        updatedAssignment.ai_grading_context += gradingInstructionsToAdd.join('');
                    }
                    setAssignments((current) => {
                        return current.map((a) => {
                            if (a.id !== updatedAssignment.id) return a;
                            return updatedAssignment;
                        });
                    });
                    await assignmentService.updateAssignment(updatedAssignment.id, omit(updatedAssignment, ['_id']));
                    previewSubmissions.forEach((ps) => {
                        const updatedSubmission = {
                            ...ps,
                            has_seen_feedback: false,
                            modified_on: timestamp,
                        };
                        submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                    });
                    setFineTuneDialog((current) => ({ ...current, changes: null }));
                    message.success(
                        t('message.assignment-updated-success-all', 'All assignment details were successfully updated.')
                    );
                    history.push(`/assignments/${currentAssignmentId}`);
                },
                onCancel: () => handleClosePushRubricDialog(),
                onClose: () => handleClosePushRubricDialog(),
            });
        }
    };

    const handleCriterionNavigation = (criterionId) => {
        setSelectedCriterionId(criterionId);
        setTab(tab === 'grades' ? 'feedback' : 'grades');
        const matchedSuggestion = form?.feedback?.improvement_suggestions?.find((s) => s.criterion_id === criterionId);
        if (matchedSuggestion) {
            setSelectedFeedbackId(matchedSuggestion.id);
        }
    };

    useEffect(() => {
        if (tab === 'feedback') setMeta({ hasSeenFeedback: true });
    }, [tab]);

    useEffect(() => {
        if (previewSubmissions?.length && !currentSubmissionId) {
            const nextSubmissionId = previewSubmissions[0].id;
            history.replace(`/assignments/${currentAssignmentId}/preview/${nextSubmissionId}`);
        }
    }, [currentAssignmentId, currentSubmissionId, history, previewSubmissions]);

    useEffect(() => {
        if (!currentSubmission?.id) return;
        setTab('grades');
    }, [currentSubmission?.id]);

    useEffect(() => {
        if (!currentAssignment) return;
        if (!currentSubmission) {
            setSelectedCriterionId(null);
            setSelectedFeedbackId(null);
            setSelectedInlineCommentId(null);
            return;
        }

        setSelectedCriterionId(currentAssignment.rubric.criteria[0].id);
        document.getElementById('actions-container').scrollTop = 0;
        if (currentSubmission.grading) {
            setForm({
                criteria: currentSubmission.grading.criteria,
                feedback: currentSubmission.grading.feedback,
                inline_comments: currentSubmission.grading.inline_comments,
                grading_explanations: currentSubmission.grading_explanations ?? {},
            });
            setMeta({ hasSeenFeedback: currentSubmission.has_seen_feedback ?? false });
            if (['rubric', 'system'].includes(currentSubmission?.ai?.feedback?.feedback_type)) {
                const activeSuggestions = currentSubmission.grading.feedback.improvement_suggestions.filter(
                    ({ active }) => active
                );
                if (activeSuggestions.length) setSelectedFeedbackId(activeSuggestions[0].id);
            }
        } else if (currentSubmission.ai) {
            setForm({
                criteria: {},
                feedback: currentSubmission.ai.feedback,
                inline_comments: currentSubmission.ai.inline_comments,
                grading_explanations: currentSubmission.grading_explanations ?? {},
            });
            setMeta({ hasSeenFeedback: currentSubmission.has_seen_feedback ?? false });
            if (['rubric', 'system'].includes(currentSubmission.ai.feedback?.feedback_type)) {
                if (currentSubmission.ai.feedback.improvement_suggestions?.length) {
                    setSelectedFeedbackId(currentSubmission.ai.feedback.improvement_suggestions[0].id);
                }
            }
        } else {
            setForm({ criteria: {}, feedback: null, inline_comments: null, grading_explanations: {} });
            setMeta({ hasSeenFeedback: true });
        }
    }, [currentAssignment, currentSubmission]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (
                message.data?.payload_type === 'regenerate_submission' &&
                (isGradesGenerating || isFeedbackGenerating)
            ) {
                const { submission } = message.data.payload;
                const updatedSubmission = {
                    ...currentSubmission,
                    modified_on: new Date().getTime(),
                };
                if (isGradesGenerating) {
                    updatedSubmission.ai.grading = submission.data.grading;
                }
                if (isFeedbackGenerating) {
                    updatedSubmission.ai.feedback = submission.data.feedback;
                    updatedSubmission.has_seen_feedback = tab === 'feedback';
                    if (updatedSubmission.grading) {
                        updatedSubmission.grading.feedback = submission.data.feedback;
                    }
                }
                setPreviewSubmissions((current) => {
                    return current.map((submission) => {
                        if (submission.id !== updatedSubmission.id) return submission;
                        submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        setTimeout(() => {
                            if (isGradesGenerating) {
                                setForm(form);
                            }
                            if (isFeedbackGenerating) {
                                setForm({ ...form, feedback: updatedSubmission.ai.feedback });
                            }
                            setGradeOverrides(gradeOverrides);
                            setMeta(meta);
                        }, 50);
                        return updatedSubmission;
                    });
                });
                setIsGradesGenerating(false);
                setIsFeedbackGenerating(false);
            }
        });
        return cleanup;
    }, [
        currentSubmission,
        form,
        gradeOverrides,
        isFeedbackGenerating,
        isGradesGenerating,
        meta,
        onMessage,
        setPreviewSubmissions,
        tab,
    ]);

    useEffect(() => {
        if (selectedCriterionId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedCriterionId]);

    useEffect(() => {
        if (selectedFeedbackId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedFeedbackId]);

    useEffect(() => {
        if (selectedInlineCommentId !== null) {
            if (tab === 'grades') {
                setSelectedCriterionId(null);
            }
            if (tab === 'feedback') {
                setSelectedFeedbackId(null);
            }
        }
    }, [selectedInlineCommentId, tab]);

    if (previewSubmissions?.length && currentSubmissionId && !currentSubmission) {
        return <Redirect to={`/assignments/${currentAssignmentId}`} />;
    }

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                previewSubmissions,
                currentSubmissionId,
                currentSubmission,
                selectedCriterionTitle,
                highlights: getHighlights(),
                form,
                setForm,
                gradeOverrides,
                setGradeOverrides,
                meta,
                tab,
                setTab,
                selectedCriterionId,
                setSelectedCriterionId,
                selectedFeedbackId,
                setSelectedFeedbackId,
                selectedInlineCommentId,
                setSelectedInlineCommentId,
                isGradesGenerating,
                isFeedbackGenerating,
                showFailedDialog,
                setShowFailedDialog,
                pushRubricDialog,
                setPushRubricDialog,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                fineTuneDialog,
                setFineTuneDialog,
                isApplyingChanges,
                setIsApplyingChanges,
                handleClickNext,
                handleSelectSubmission,
                handleClickRegenerateFeedback,
                handleSelectDropdown,
                handleClickExit,
                handleClickFineTune,
                handleClickApplyChanges,
                handleCriterionNavigation,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
